import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Tooltip,
  IconButton,
  Dialog,
  Pagination,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccessTime,
  Quiz as QuizIcon,
  Replay,
  PlayArrow,
  Search,
  BarChart as BarChartIcon,
  PauseCircle,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../api';
import useExamMode from '../../../hooks/useExamMode';
import ScanUploadDialog from '../../../components/scan/ScanUploadDialog';
import downloadPaperPdf from '../../../components/scan/downloadPaperPdf';
import TargetSchoolFilterSelect, { matchesTargetSchools } from '../components/TargetSchoolFilterSelect';
import CatalogueFilterBar, { VISIBILITY_DEFAULT, matchesVisibility } from '../components/CatalogueFilterBar';
import { SCHOOL_TYPE_OPTIONS, schoolMatchesCategory } from '../../../utils/schoolCategoryFilter';
import PanelPackOffer from '../components/PanelPackOffer';
import { completePanelPurchase, PANEL_PURCHASE_MESSAGES } from '../../../utils/panelPurchase';
import { usePanelBasket } from '../components/PanelBasket';
import { catalogueCountLabel } from '../components/catalogueMetrics';
import useStudentPortalMode from "../../../hooks/useStudentPortalMode";

const TAB_EXAM = 0;
const TAB_RESULT = 1;
const EXAMS_PER_PAGE = 12;

// Same four values and wording as the past papers catalogue.
const SHOW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'available', label: 'Free & purchased' },
  { value: 'purchased', label: 'Purchased' },
  { value: 'buy', label: 'To buy' },
];

// One line per status, so "no results" never reads as "nothing exists".
const EMPTY_BY_SHOW = {
  all: 'are currently available',
  available: 'are ready to start',
  purchased: 'purchased yet',
  buy: 'left to buy',
};

const resultsTableSx = {
  '& thead': { display: { xs: 'none', sm: 'table-header-group' } },
  '& tbody tr': {
    display: { xs: 'block', sm: 'table-row' },
    px: { xs: 1.5, sm: 0 },
    py: { xs: 1, sm: 0 },
    borderBottom: { xs: '1px solid rgba(0,0,0,0.08)', sm: 'none' },
  },
  '& tbody td': {
    display: { xs: 'flex', sm: 'table-cell' },
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    px: { xs: 0 },
    py: { xs: 0.75 },
    borderBottom: { xs: 'none' },
    textAlign: { xs: 'right', sm: 'inherit' },
    '&::before': {
      content: { xs: 'attr(data-label)', sm: 'none' },
      fontWeight: 700,
      fontSize: '0.75rem',
      color: 'text.secondary',
      textAlign: 'left',
    },
  },
  '& tbody td:first-of-type': { textAlign: 'left', '&::before': { content: 'none' } },
  '& tbody td:last-of-type': { justifyContent: { xs: 'flex-end', sm: 'initial' } },
};

const StudentMockExams = () => {
  // Course + plan students: say which side this list is for (see DashboardViewToggle).
  const { canToggleDashboard, dashboardView, catalogueLocked } = useStudentPortalMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { isTestingMode } = useExamMode();
  const isTestMode = isTestingMode('mock_exams');
  const isTesterExams = localStorage.getItem("role") === "tester";
  const [tab, setTab] = useState(TAB_EXAM);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [starting, setStarting] = useState(null);
  // Every mock exam is listed; this narrows to the ones open now or the ones to buy.
  // Opens on what the student already has (free + purchased); "To buy" switches to the shop.
  const [showFilter, setShowFilter] = useState('available');
  const [purchaseRefreshKey, setPurchaseRefreshKey] = useState(0);
  const { items: basketItems, addItem: addToBasket, clear: clearBasket, basketUi } = usePanelBasket();
  // Tester-only: inactive exams are served to testers alone.
  const [visibilityFilter, setVisibilityFilter] = useState(VISIBILITY_DEFAULT);
  const [filterSubject, setFilterSubject] = useState('');
  const [filterSchoolIds, setFilterSchoolIds] = useState([]);
  const [filterSchoolCategory, setFilterSchoolCategory] = useState('independent');
  const [schools, setSchools] = useState([]);
  const [search, setSearch] = useState('');
  const [examPage, setExamPage] = useState(1);
  const inProgressToastShownRef = useRef(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resumeDialogExam, setResumeDialogExam] = useState(null);

  // Result tab: one row per exam (averages)
  const [examSummaries, setExamSummaries] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [resultSearch, setResultSearch] = useState('');
  const [resultSearchSubmitted, setResultSearchSubmitted] = useState('');
  const [showPracticeResults, setShowPracticeResults] = useState(false);
  const [attemptsPagination, setAttemptsPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
  });

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const fetchExamSummaries = useCallback(async (page = 1, searchTerm = '', includeTest = false, schoolIds = []) => {
    setAttemptsLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (searchTerm) params.search = searchTerm;
      if (includeTest) params.include_test = 1;
      if (schoolIds?.length) params.target_school_ids = schoolIds.join(',');
      const res = await api.get('/student/mock-exam/results-by-exam', { params });
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
      setExamSummaries(list);
      setAttemptsPagination({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? 25,
        total: payload?.total ?? 0,
      });
    } catch (err) {
      toast.error('Failed to load results');
      setExamSummaries([]);
    } finally {
      setAttemptsLoading(false);
    }
  }, []);

  // Load the results up front rather than on the first click of the Result tab: the fetch used to
  // start only once the tab was already open, so the student watched a spinner every time.
  useEffect(() => {
    const ids = filterSchoolIds.length
      ? filterSchoolIds
      : (!filterSchoolCategory || !schools.length
        ? []
        : schools
          .filter((school) => schoolMatchesCategory(school, filterSchoolCategory))
          .map((school) => String(school.id)));
    fetchExamSummaries(attemptsPagination.current_page, resultSearchSubmitted, showPracticeResults, ids);
  }, [resultSearchSubmitted, attemptsPagination.current_page, showPracticeResults, filterSchoolIds, filterSchoolCategory, schools, fetchExamSummaries]);

  useEffect(() => {
    // Auto-enable practice results filter while in practice mode.
    if (isTestMode && !showPracticeResults) {
      setShowPracticeResults(true);
      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
    } else if (!isTestMode && showPracticeResults) {
      setShowPracticeResults(false);
      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
    }
  }, [isTestMode, showPracticeResults]);

  useEffect(() => {
    const urlTab = new URLSearchParams(location.search || '').get('tab');
    if (urlTab === 'exam') {
      setTab(TAB_EXAM);
      return;
    }
    if (urlTab === 'result') {
      setTab(TAB_RESULT);
      return;
    }

    if (location.state?.openMockExamResult) {
      setTab(TAB_RESULT);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, location.state, location.pathname]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // include_purchasable: list exams the student doesn't have yet too, so they can buy here.
      const examsRes = await api.get('/student/mock-exams', { params: { include_purchasable: 1 } });
      const examList = examsRes.data?.data || [];
      // Server order matters: exams for the student's own target schools come first.
      setExams(examList);
      // Show toast if any exam is in progress (show once per mount)
      if (!inProgressToastShownRef.current && examList.some((e) => e.has_in_progress)) {
        inProgressToastShownRef.current = true;
        toast.info('You have an exam in progress. Resume it from the card below.', {
          toastId: 'in-progress-exam',
          autoClose: 5000,
        });
      }
    } catch (err) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Back from Stripe: record the purchase first, so the exam arrives already unlocked.
    completePanelPurchase().then((outcome) => {
      if (cancelled) return;
      if (outcome) {
        (outcome === 'cancelled' ? toast.info : toast.success)(PANEL_PURCHASE_MESSAGES[outcome]);
        if (outcome !== 'cancelled') {
          clearBasket();
          setPurchaseRefreshKey((k) => k + 1);
        }
      }
      fetchData();
    });
    return () => {
      cancelled = true;
    };
  }, [fetchData, clearBasket]);

  // Collect it; the parent authorises and pays for everything in one step from
  // the basket bar, rather than once per exam.
  const handleBuyExam = (exam) => {
    addToBasket({
      resourceType: 'mock_exam',
      resourceId: exam.id,
      name: exam.name,
      price: Number(String(exam.price ?? '').replace(/[^0-9.]/g, '')) || 0,
      priceDisplay: exam.price_display || exam.price_label || null,
    });
  };

  useEffect(() => {
    api.get('/common/data', { params: { param: 'Schools', linked_to: 'mock_exams' } })
      .then((schRes) => {
        const list = schRes.data?.data || schRes.data || [];
        setSchools(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const focusId = new URLSearchParams(location.search || "").get("focusMockExam");
    if (!focusId || tab !== TAB_EXAM || loading || exams.length === 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const safeId = String(focusId).replace(/[^0-9]/g, "");
      if (!safeId) return;
      // The list is paged, so a deep-linked exam is usually not on the page being shown — move to
      // the page holding it before looking for its card.
      const pos = filteredExams.findIndex((e) => String(e.id) === safeId);
      if (pos >= 0) {
        const page = Math.floor(pos / EXAMS_PER_PAGE) + 1;
        if (page !== examPage) {
          setExamPage(page);
          return;
        }
      }
      const el = document.querySelector(`[data-mock-exam-id="${safeId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.search, tab, loading, exams, examPage]);

  const handleStart = async (exam, startFresh = false, isPausedResume = false) => {
    setStarting(exam.id);
    try {
      const res = await api.post(`/student/mock-exam/${exam.id}/start`, startFresh ? { start_fresh: true } : {});
      const sessionData = res.data?.data;
      if (startFresh) {
        sessionStorage.removeItem(`examAnswers_${exam.id}`);
        sessionStorage.removeItem(`examQuestion_${exam.id}`);
        sessionStorage.removeItem(`examScratch_${exam.id}`);
        sessionStorage.removeItem(`examFlagged_${exam.id}`);
        sessionStorage.removeItem(`examPaused_${exam.id}`);
      }
      navigate(`/student/mock-exam/${exam.id}/take`, { state: { session: sessionData, startFresh, isPausedResume, isTestMode: sessionData?.is_test_mode || isTestMode } });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start exam');
    } finally {
      setStarting(null);
    }
  };

  const handleExamButtonClick = (exam) => {
    if (exam.has_in_progress) {
      // Always show popup for any in-progress attempt (paused or not)
      setResumeDialogExam(exam);
      setResumeDialogOpen(true);
    } else {
      // New exam or retake — go directly
      handleStart(exam);
    }
  };

  const handleResumeChoice = async (choice) => {
    if (!resumeDialogExam) return;
    const exam = resumeDialogExam;
    const isPaused = exam.has_in_progress && !!sessionStorage.getItem(`examPaused_${exam.id}`);
    setResumeDialogOpen(false);
    setResumeDialogExam(null);
    if (choice === 'startFresh') {
      await handleStart(exam, true, false);
    } else {
      // Resume — if the exam was paused, load it back in paused state so timer waits
      await handleStart(exam, false, isPaused);
    }
  };

  const subjectOptions = useMemo(() => {
    const names = new Set();
    exams.forEach((e) => {
      (e.subjects ?? []).forEach((s) => { if (s) names.add(s); });
    });
    return Array.from(names).sort();
  }, [exams]);

  const schoolNameById = useMemo(() => {
    const map = new Map();
    schools.forEach((s) => {
      if (s?.id != null && s.name) map.set(String(s.id), String(s.name).toLowerCase());
    });
    return map;
  }, [schools]);

  const schoolsOfType = useMemo(
    () => schools.filter((school) => schoolMatchesCategory(school, filterSchoolCategory)),
    [schools, filterSchoolCategory]
  );
  const categorySchoolIds = useMemo(
    () => schoolsOfType.map((school) => String(school.id)),
    [schoolsOfType]
  );

  const filteredExamsUnsorted = exams.filter((exam) => {
    // One box covers the exam title, its subject, and the school — the same cut parents
    // use on other 11+ catalogues, where "Kent maths" or "Highgate" is the search.
    const haystack = [
      exam.name,
      exam.school_name,
      exam.paper_label,
      exam.bundle_label,
      ...(exam.subjects ?? []),
      ...(exam.matched_target_schools ?? []),
      ...(exam.target_school_ids ?? []).map((id) => schoolNameById.get(String(id))),
    ].filter(Boolean).join(' ').toLowerCase();
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matchesSearch = terms.every((term) => haystack.includes(term));
    const matchesSubject = !filterSubject || (exam.subjects ?? []).includes(filterSubject);
    const matchesCategory = !filterSchoolCategory || !schools.length
      || (categorySchoolIds.length > 0 && matchesTargetSchools(exam, categorySchoolIds));
    const matchesSchool = matchesTargetSchools(exam, filterSchoolIds);
    // Older API responses have no is_available: treat those rows as open.
    const isOpen = exam.is_available !== false || Boolean(exam.is_purchased);
    const matchesShow =
      showFilter === 'all' ||
      (showFilter === 'available' && isOpen) ||
      (showFilter === 'buy' && !isOpen) ||
      (showFilter === 'purchased' && Boolean(exam.is_purchased));
    const matchesVis = !isTesterExams || matchesVisibility(visibilityFilter, exam.is_inactive);
    return matchesSearch && matchesSubject && matchesCategory && matchesSchool && matchesShow && matchesVis;
  });

  // Bought exams first, then everything else they can open now (free previews, plan or pack
  // access), then the ones to buy — so "All" opens on what the student can start today.
  // The API's order is kept within each group (Array.sort is stable). The whole list is
  // loaded at once and paged here, so this reaches every page.
  const accessRank = (exam) => {
    if (exam.is_purchased) return 0;
    if (exam.is_free_exam || exam.is_available !== false) return 1;
    return 2;
  };
  const filteredExams = [...filteredExamsUnsorted].sort((a, b) => accessRank(a) - accessRank(b));

  // The exam list is paged client-side; every filter narrows it, so the student must land back on
  // page 1 or a filter that leaves three exams shows an empty page 4.
  useEffect(() => {
    setExamPage(1);
  }, [search, filterSubject, filterSchoolIds, filterSchoolCategory, showFilter, visibilityFilter]);

  const examPageCount = Math.max(1, Math.ceil(filteredExams.length / EXAMS_PER_PAGE));
  const safeExamPage = Math.min(examPage, examPageCount);
  const pagedExams = filteredExams.slice((safeExamPage - 1) * EXAMS_PER_PAGE, safeExamPage * EXAMS_PER_PAGE);

  const handleSchoolCategoryChange = (value) => {
    setFilterSchoolCategory(value);
    setFilterSchoolIds((ids) =>
      ids.filter((id) => {
        const school = schools.find((s) => String(s.id) === String(id));
        return school && schoolMatchesCategory(school, value);
      })
    );
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSchoolFilterChange = (ids) => {
    setFilterSchoolIds(ids);
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    setSearch('');
    setFilterSubject('');
    setFilterSchoolCategory('independent');
    setShowFilter('available');
    setVisibilityFilter(VISIBILITY_DEFAULT);
    handleSchoolFilterChange([]);
  };

  const handleResultSearch = (e) => {
    e?.preventDefault();
    setResultSearchSubmitted(resultSearch);
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleResultClear = () => {
    setResultSearch('');
    setResultSearchSubmitted('');
    setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const formatResultDate = (iso) => {
    if (!iso) return 'N/A';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const [downloadingExamId, setDownloadingExamId] = useState(null);
  const [scanExam, setScanExam] = useState(null);
  const [allowPaperDownload, setAllowPaperDownload] = useState(false);
  const [allowScanUpload, setAllowScanUpload] = useState(false);
  const [myTargetSchools, setMyTargetSchools] = useState([]);

  useEffect(() => {
    api.get('/student/settings')
      .then((res) => {
        const data = res.data?.data || {};
        setAllowPaperDownload(data.allow_paper_download === true);
        setAllowScanUpload(data.allow_scan_upload === true);
        setMyTargetSchools(Array.isArray(data.target_schools) ? data.target_schools : []);
      })
      .catch(() => {});
  }, []);

  const handleDownloadExam = async (exam) => {
    setDownloadingExamId(exam.id);
    try {
      await downloadPaperPdf(`/student/mock-exam/${exam.id}/download-pdf`, `${exam.name || 'mock-exam'}.pdf`);
    } finally {
      setDownloadingExamId(null);
    }
  };

  const handleScanComplete = (res) => {
    const examId = res?.subject_id ?? scanExam?.id;
    setScanExam(null);
    fetchData();
    navigate(`/student/mock-exam/exam/${examId}/report`, { state: { scannedSubmission: res } });
  };

  if (loading && tab === TAB_EXAM) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (loadError && tab === TAB_EXAM) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 420 }}>
          Unable to load exams. Please check your connection and try again.
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Free trial ended with no plan: only items already bought open here. */}
      {catalogueLocked && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2, alignItems: "center", flexWrap: { xs: "wrap", sm: "nowrap" } }}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}/choose-plan?plans=1`)}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Choose a plan
            </Button>
          }
        >
          Your free trial has ended. Choose a plan to unlock mock exams again. Anything you bought stays open.
        </Alert>
      )}
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Exam" value={TAB_EXAM} icon={<QuizIcon />} iconPosition="start" />
        <Tab label="Result" value={TAB_RESULT} icon={<BarChartIcon />} iconPosition="start" />
      </Tabs>

      {isTestMode && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — Practice freely. Answers will not be graded.
        </Alert>
      )}

      {tab === TAB_EXAM && (
        <Box>
          {/* Page Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Mock Exams
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTestMode
                ? 'Practice mode is active. Your mock exam results will not be recorded.'
                : canToggleDashboard && dashboardView === 'mobile'
                  ? 'Your mobile plan library. Every exam here is included in your plan.'
                  : canToggleDashboard
                    ? 'Your course exams. Buy any other exam to add it to your course side.'
                    : 'Start the exams you have, or buy any other exam right here.'}
            </Typography>
          </Box>

          {!isTestMode && <PanelPackOffer resourceType={2} refreshKey={purchaseRefreshKey} />}

          <CatalogueFilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by school, subject or exam"
            status={{
              value: showFilter,
              onChange: setShowFilter,
              options: SHOW_OPTIONS,
            }}
            subject={
              subjectOptions.length > 0
                ? {
                    value: filterSubject,
                    onChange: setFilterSubject,
                    options: subjectOptions.map((s) => ({ value: s, label: s })),
                  }
                : undefined
            }
            schoolCategory={{
              value: filterSchoolCategory,
              onChange: handleSchoolCategoryChange,
              options: SCHOOL_TYPE_OPTIONS,
            }}
            visibility={isTesterExams ? { value: visibilityFilter, onChange: setVisibilityFilter } : undefined}
            schools={schoolsOfType}
            schoolValue={filterSchoolIds}
            onSchoolChange={handleSchoolFilterChange}
            targetSchools={myTargetSchools}
            schoolLabelId="mock-exam-target-school-filter"
            resultCount={filteredExams.length}
            onClearAll={handleClearFilters}
          />

      {/* In-progress resume banner (examplify-style) */}
      {filteredExams.some((e) => e.has_in_progress) && (
        <Alert severity="info" icon={false} sx={{ mb: 3, borderRadius: 2, bgcolor: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', '& .MuiAlert-message': { fontWeight: 600 } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            You have {filteredExams.filter((e) => e.has_in_progress).length === 1
              ? 'an exam'
              : `${filteredExams.filter((e) => e.has_in_progress).length} exams`} in progress.
            Resume from the exam card below to continue where you left off.
          </Typography>
        </Alert>
      )}

      {/* Exam List — flat grid */}
      {filteredExams.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No mock exams {EMPTY_BY_SHOW[showFilter] || EMPTY_BY_SHOW.all}{search || filterSubject || filterSchoolIds.length || filterSchoolCategory !== 'independent' || (isTesterExams && visibilityFilter !== VISIBILITY_DEFAULT) ? ' matching your filters' : ''}.
        </Alert>
      ) : (
        <>
        <Grid container spacing={2}>
          {pagedExams.map((exam) => {
            const isStarting = starting === exam.id;
            const noAttempts = !isTestMode && exam.attempts_left === 0 && !exam.has_in_progress;
            const hasAttempted = exam.has_attempted || (exam.attempts_left !== undefined && exam.attempts_left < (exam.retake_attempts || 2));
            const isPaused = exam.has_in_progress && !!sessionStorage.getItem(`examPaused_${exam.id}`);
            // A free exam is never sold: showing a £0 "Add to basket" next to its Free tag was confusing.
            const toBuy = exam.is_available === false && !exam.is_purchased && !exam.is_free_exam;
            const inBasket = basketItems.some(
              (row) => row.resourceType === 'mock_exam' && Number(row.resourceId) === Number(exam.id)
            );
            const countLabel = catalogueCountLabel({
              subjects: exam.subjects,
              questionsCount: exam.questions_count,
              sectionsCount: exam.sections_count,
            });

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={exam.id}>
                <Card
                  data-mock-exam-id={exam.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: 'box-shadow 0.2s',
                    border: exam.has_in_progress ? '2px solid #7c3aed' : '1px solid #e0e0e0',
                    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                  }}
                >
                  <CardContent sx={{ p: 2, pb: '12px !important', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {/* Status chips */}
                    {(exam.has_in_progress || isPaused || exam.is_inactive) && (
                      <Box sx={{ mb: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {/* Only testers are served Inactive exams: set Inactive in admin, not on sale yet. */}
                        {exam.is_inactive && (
                          <Chip label="Inactive · tester preview" size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: '#FFF4E0', color: '#B4690E', border: '1px solid #F3D19E' }} />
                        )}
                        {exam.has_in_progress && !isPaused && (
                          <Chip label="In Progress" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd' }} />
                        )}
                        {isPaused && (
                          <Chip icon={<PauseCircle sx={{ fontSize: 12 }} />} label="Paused" size="small" color="info" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                        )}
                      </Box>
                    )}

                    {/* Exam name */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.75 }}>
                      {exam.name}
                    </Typography>

                    {/* Compact info row */}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                      {countLabel ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <QuizIcon sx={{ fontSize: 13 }} /> {countLabel.value} {countLabel.unit}
                      </Typography>
                      ) : null}
                      {exam.duration_minutes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <AccessTime sx={{ fontSize: 13 }} /> {exam.duration_minutes}m
                        </Typography>
                      )}
                      {isTestMode ? (
                        <Chip label="Unlimited Practice" size="small" color="info" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                      ) : exam.attempts_left !== undefined && (
                        <Typography variant="caption" color={noAttempts ? 'error' : 'text.secondary'} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <Replay sx={{ fontSize: 13 }} /> {exam.attempts_left} left
                        </Typography>
                      )}
                      {(exam.matched_target_schools || []).map((schoolName) => (
                        <Chip
                          key={schoolName}
                          label={schoolName}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, maxWidth: '100%' }}
                        />
                      ))}
                      {exam.is_free_exam && (
                        <Chip label="Free" size="small" color="success" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                      )}
                      {exam.is_purchased && (
                        <Chip label="Purchased" size="small" color="success" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                      )}
                      {!toBuy && !exam.is_purchased && exam.access_label && !exam.is_free_exam && (
                        <Chip label={exam.access_label} size="small" color="success" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                      )}
                      {isTesterExams && exam.total_marks > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontWeight: 600 }}>
                          {exam.total_marks}m
                        </Typography>
                      )}
                    </Box>

                    {/* Action button */}
                    {toBuy ? (
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600, color: '#4c1d95', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                        {exam.price_display}
                      </Typography>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => handleBuyExam(exam)}
                        disabled={inBasket}
                        startIcon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          background: 'linear-gradient(90deg, #7c3aed, #6d28d9)',
                          textTransform: 'none',
                          fontWeight: 600,
                          py: 0.75,
                          borderRadius: 1.5,
                          fontSize: '0.82rem',
                          '&:hover': { opacity: 0.9 },
                          '&:disabled': { background: '#ccc', color: 'white' },
                        }}
                      >
                        {inBasket ? 'In basket' : 'Add to basket'}
                      </Button>
                    </Box>
                    ) : (
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 0.75, alignItems: 'stretch' }}>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={() => handleExamButtonClick(exam)}
                        disabled={isStarting || noAttempts}
                        startIcon={
                          isStarting ? <CircularProgress size={14} color="inherit" />
                          : isPaused ? <PauseCircle sx={{ fontSize: 16 }} />
                          : exam.has_in_progress ? <PlayArrow sx={{ fontSize: 16 }} />
                          : hasAttempted ? <Replay sx={{ fontSize: 16 }} />
                          : <PlayArrow sx={{ fontSize: 16 }} />
                        }
                        sx={{
                          background: noAttempts ? '#bdbdbd'
                            : isPaused ? 'linear-gradient(90deg, #667eea, #764ba2)'
                            : exam.has_in_progress ? 'linear-gradient(90deg, #667eea, #764ba2)'
                            : 'linear-gradient(90deg, #1a1a1a, #3a3a3a)',
                          textTransform: 'none',
                          fontWeight: 600,
                          py: 0.75,
                          borderRadius: 1.5,
                          fontSize: '0.82rem',
                          '&:hover': { opacity: 0.9 },
                          '&:disabled': { background: '#ccc', color: 'white' },
                        }}
                      >
                        {isStarting ? 'Starting...'
                          : noAttempts ? 'No Attempts'
                          : isPaused ? 'Resume (Paused)'
                          : exam.has_in_progress ? 'Resume'
                          : isTestMode ? 'Start Practice'
                          : hasAttempted ? 'Retake'
                          : 'Start'}
                      </Button>
                      {allowPaperDownload && (
                      <Tooltip title="Download this exam to print">
                        <span>
                          <IconButton
                            size="small"
                            disabled={downloadingExamId === exam.id}
                            onClick={() => handleDownloadExam(exam)}
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5 }}
                          >
                            {downloadingExamId === exam.id
                              ? <CircularProgress size={16} />
                              : <DownloadIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                      {allowScanUpload && (
                      <Tooltip
                        title={isTestMode
                          ? "Scan marking isn't available in practice mode"
                          : noAttempts
                            ? 'No attempts left'
                            : 'Upload a photo of the exam you wrote'}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={isTestMode || noAttempts}
                            onClick={() => setScanExam(exam)}
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5 }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      )}
                    </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {examPageCount > 1 && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {(safeExamPage - 1) * EXAMS_PER_PAGE + 1}–{Math.min(safeExamPage * EXAMS_PER_PAGE, filteredExams.length)} of {filteredExams.length} exams
            </Typography>
            <Pagination
              count={examPageCount}
              page={safeExamPage}
              onChange={(_, page) => {
                setExamPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              color="primary"
              shape="rounded"
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>
        )}
        </>
      )}
        </Box>
      )}

      {scanExam && (
        <ScanUploadDialog
          open
          onClose={() => setScanExam(null)}
          title={scanExam.name}
          endpoints={{
            validate: `/student/mock-exam/${scanExam.id}/scan/validate`,
            upload: `/student/mock-exam/${scanExam.id}/scan`,
            status: (id) => `/student/mock-exam/scan/status/${id}`,
          }}
          onComplete={handleScanComplete}
        />
      )}

      {/* Resume Exam Dialog — shown when clicking Resume/Start on exam with in-progress attempt */}
      <Dialog open={resumeDialogOpen} onClose={() => { setResumeDialogOpen(false); setResumeDialogExam(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Resume Exam?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have an unfinished attempt for this exam. Resume from where you left off, or start fresh?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleResumeChoice('startFresh')}
            sx={{ textTransform: 'none' }}
          >
            Start Fresh
          </Button>
          <Button
            variant="contained"
            onClick={() => handleResumeChoice('resume')}
            sx={{ textTransform: 'none' }}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>

      {tab === TAB_RESULT && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                My Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One row per exam with your average score. Open Report to see every attempt and averages.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
              <Box component="form" onSubmit={handleResultSearch} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Search results"
                  value={resultSearch}
                  onChange={(e) => setResultSearch(e.target.value)}
                  sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: { sm: 220 }, maxWidth: { xs: '100%', sm: 'none' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" size="medium">
                  Search
                </Button>
              </Box>
              {resultSearchSubmitted && (
                <Button variant="outlined" size="small" onClick={handleResultClear}>
                  Clear
                </Button>
              )}
              {schoolsOfType.length > 0 && (
                <TargetSchoolFilterSelect
                  schools={schoolsOfType}
                  value={filterSchoolIds}
                  onChange={handleSchoolFilterChange}
                  labelId="mock-exam-result-target-school-filter"
                  targetSchools={myTargetSchools}
                  category={filterSchoolCategory}
                />
              )}
              {isTestMode && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <Typography variant="body2" color="text.secondary">Show practice results</Typography>
                  <input
                    type="checkbox"
                    checked={showPracticeResults}
                    onChange={(e) => {
                      setShowPracticeResults(e.target.checked);
                      setAttemptsPagination((prev) => ({ ...prev, current_page: 1 }));
                    }}
                  />
                </Box>
              )}
            </Box>
            {attemptsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : examSummaries.length > 0 ? (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                  {/* Below sm each row stacks into a card (label: value), so phones never scroll sideways. */}
                  <Table size="medium" sx={resultsTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Exam</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last completed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Attempts</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Average score</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {examSummaries.map((row) => (
                        <TableRow key={row.mock_exam_id}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.exam_name}
                                </Typography>
                                {row.has_practice_attempts && (
                                  <Chip
                                    size="small"
                                    label="Includes practice"
                                    color="info"
                                    sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                              </Box>
                              {row.category_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {row.category_name}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell data-label="Last completed">{formatResultDate(row.last_completed_at)}</TableCell>
                          <TableCell data-label="Attempts">{row.attempt_count}</TableCell>
                          <TableCell data-label="Average score">
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Tooltip title="Mean percentage across attempts (AI-adjusted when available)">
                                <Chip
                                  size="small"
                                  icon={<BarChartIcon sx={{ fontSize: 13 }} />}
                                  label={`Avg ${typeof row.avg_percentage === 'number' ? row.avg_percentage : Number(row.avg_percentage).toFixed(1)}%`}
                                  color={Number(row.avg_percentage) >= 50 ? 'success' : 'error'}
                                  sx={{ fontWeight: 600 }}
                                />
                              </Tooltip>
                              {row.total_marks != null && row.total_marks > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                                  Avg marks: {Number(row.avg_score ?? 0).toFixed(1)} / {Number(row.total_marks).toFixed(0)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                navigate(`/student/mock-exam/exam/${row.mock_exam_id}/report`, {
                                  state: { includePracticeResults: showPracticeResults },
                                })
                              }
                            >
                              Report
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={attemptsPagination.current_page <= 1}
                    onClick={() =>
                      setAttemptsPagination((prev) => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))
                    }
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={attemptsPagination.current_page >= attemptsPagination.last_page}
                    onClick={() =>
                      setAttemptsPagination((prev) => ({
                        ...prev,
                        current_page: Math.min(prev.last_page, prev.current_page + 1),
                      }))
                    }
                  >
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Results Found
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Complete an exam to see your results here.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {basketUi}
    </Box>
  );
};

export default StudentMockExams;
